import { ClickAwayListener, InputAdornment, Popper } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import SearchIcon from '@mui/icons-material/Search'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { ICustomProperty } from '../../generated-types/customProperty'
import { resolveCustomPropertyLabel } from '../../utils/customPropertyValues'
import { getFieldTypeMeta, useEquipmentFieldPicker } from './EquipmentFieldPicker.action'
import {
  CountChip,
  DragHint,
  DropdownEmpty,
  DropdownHeader,
  DropdownList,
  DropdownOption,
  DropdownOptionAdd,
  DropdownPanel,
  EmptyState,
  EmptyStateSubtitle,
  EmptyStateTitle,
  EndDropZone,
  FieldName,
  FieldRow,
  GripHandle,
  PickerContainer,
  PositionLabel,
  RowsContainer,
  SearchField,
  SelectedPanel,
  SelectedPanelHeader,
  TypeLabel,
} from './EquipmentFieldPicker.style'

interface EquipmentFieldPickerProps {
  customProperties: ICustomProperty[]
  setCustomProperties: React.Dispatch<React.SetStateAction<ICustomProperty[]>>
  data: ICustomProperty[]
  lockedCustomPropertyIds?: number[]
}

export const EquipmentFieldPicker: React.FC<EquipmentFieldPickerProps> = ({
  customProperties,
  setCustomProperties,
  data,
  lockedCustomPropertyIds = [],
}) => {
  const { t: translate } = useTranslation(['main-component-type', 'custom-property'])
  const {
    pickerRef,
    searchInputRef,
    query,
    setQuery,
    pickerOpen,
    setPickerOpen,
    draggingId,
    setDraggingId,
    available,
    addProperty,
    removeProperty,
    reorder,
    moveToEnd,
  } = useEquipmentFieldPicker({ customProperties, setCustomProperties, data, lockedCustomPropertyIds })

  return (
    <>
      <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
        <PickerContainer ref={pickerRef}>
          <SearchField
            inputRef={searchInputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setPickerOpen(true)}
            placeholder={translate('searchFieldPlaceholder', { ns: 'main-component-type' })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Popper
            open={pickerOpen}
            anchorEl={pickerRef.current}
            placement="bottom-start"
            style={{ width: pickerRef.current?.clientWidth, zIndex: 1400 }}
          >
            <DropdownPanel>
              <DropdownHeader>
                {(query.trim() ? translate('matchingFields') : translate('availableFields')) + ' · ' + available.length}
              </DropdownHeader>
              <DropdownList>
                {available.map(property => (
                  <DropdownOption key={property.id} onClick={() => addProperty(property)}>
                    <FieldName>{resolveCustomPropertyLabel(property)}</FieldName>
                    <DropdownOptionAdd>+ {translate('add', { ns: 'common' })}</DropdownOptionAdd>
                  </DropdownOption>
                ))}
                {available.length === 0 && <DropdownEmpty>{translate('allFieldsAlreadyAdded')}</DropdownEmpty>}
              </DropdownList>
            </DropdownPanel>
          </Popper>
        </PickerContainer>
      </ClickAwayListener>

      <SelectedPanel>
        <SelectedPanelHeader>
          <FieldName>{translate('selectedFields')}</FieldName>
          <CountChip>{customProperties.length}</CountChip>
          <DragHint>
            <DragIndicatorIcon fontSize="small" /> {translate('dragToReorder')}
          </DragHint>
        </SelectedPanelHeader>
        <RowsContainer>
          {customProperties.map((property, index) => {
            const meta = getFieldTypeMeta(property.controllerType)
            const isDragging = property.id === draggingId
            const isLocked = lockedCustomPropertyIds.includes(property.id)
            return (
              <FieldRow
                key={property.id}
                isDragging={isDragging}
                draggable={!isLocked}
                onDragStart={e => {
                  e.dataTransfer.effectAllowed = 'move'
                  setDraggingId(property.id)
                }}
                onDragEnter={() => {
                  if (draggingId != null && draggingId !== property.id) reorder(draggingId, property.id)
                }}
                onDragOver={e => e.preventDefault()}
                onDragEnd={() => setDraggingId(null)}
              >
                <GripHandle isDragging={isDragging} isLocked={isLocked}>
                  <DragIndicatorIcon fontSize="small" />
                </GripHandle>
                <PositionLabel>{index + 1}</PositionLabel>
                <FieldName>{resolveCustomPropertyLabel(property)}</FieldName>
                <TypeLabel>{translate(meta.labelKey, { ns: 'custom-property' })}</TypeLabel>
                {!isLocked && (
                  <GridActionButton
                    width="28px"
                    height="28px"
                    toolTipTitle={translate('removeField')}
                    onClick={() => removeProperty(property.id)}
                    buttonIcon={<CloseIcon fontSize="small" />}
                  />
                )}
              </FieldRow>
            )
          })}
          {customProperties.length > 0 && (
            <EndDropZone
              onDragEnter={() => {
                if (draggingId != null) moveToEnd(draggingId)
              }}
              onDragOver={e => e.preventDefault()}
            />
          )}
          {customProperties.length === 0 && (
            <EmptyState>
              <EmptyStateTitle>{translate('noFieldsAdded')}</EmptyStateTitle>
              <EmptyStateSubtitle>{translate('useSearchAboveToAddFields')}</EmptyStateSubtitle>
            </EmptyState>
          )}
        </RowsContainer>
      </SelectedPanel>
    </>
  )
}
